<template>
  <div class="reports-page">
    <!-- Page Header -->
    <div class="page-header mb-6">
      <div class="d-flex justify-space-between align-center flex-wrap">
        <div>
          <h1 class="text-h4 font-weight-bold text-primary mb-2">Reports & Analytics</h1>
          <p class="text-body-1 text-medium-emphasis">
            Comprehensive insights into board meetings, attendance, and resolutions
          </p>
        </div>
      </div>
    </div>

    <!-- Date Range Filter -->
    <v-card class="mb-6">
      <v-card-title>
        <v-icon class="mr-2">mdi-calendar-range</v-icon>
        Date Range Filter
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="dateRange.start"
              label="Start Date"
              type="date"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="dateRange.end"
              label="End Date"
              type="date"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedPeriod"
              :items="periodOptions"
              label="Quick Select"
              variant="outlined"
              density="compact"
              @update:model-value="updateDateRange"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-btn
              color="primary"
              block
              @click="refreshReports"
              :loading="loading"
            >
              <v-icon start>mdi-refresh</v-icon>
              Refresh Reports
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="text-h6 mt-4">Loading reports...</p>
    </div>

    <div v-else>
      <!-- Report Tabs -->
      <v-card>
        <v-tabs
          v-model="activeTab"
          bg-color="primary"
          color="white"
        >
          <v-tab value="attendance">
            <v-icon start>mdi-account-check</v-icon>
            Attendance
          </v-tab>
          <v-tab value="meetings">
            <v-icon start>mdi-calendar-multiselect</v-icon>
            Meetings
          </v-tab>
          <v-tab value="resolutions">
            <v-icon start>mdi-file-document-multiple</v-icon>
            Resolutions
          </v-tab>
          <v-tab value="comprehensive">
            <v-icon start>mdi-chart-box</v-icon>
            Comprehensive
          </v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <!-- Attendance Report Tab -->
          <v-window-item value="attendance">
            <v-card-text>
              <div class="d-flex justify-space-between align-center mb-4">
                <h3 class="text-h6">Member Attendance Analysis</h3>
                <div class="d-flex gap-2">
                  <v-btn
                    color="success"
                    variant="outlined"
                    prepend-icon="mdi-file-delimited"
                    @click="exportReport('attendance', 'csv')"
                    :loading="exporting"
                  >
                    CSV
                  </v-btn>
                  <v-btn
                    color="success"
                    variant="outlined"
                    prepend-icon="mdi-file-excel"
                    @click="exportReport('attendance', 'excel')"
                    :loading="exporting"
                  >
                    Excel
                  </v-btn>
                  <v-btn
                    color="error"
                    variant="outlined"
                    prepend-icon="mdi-file-pdf-box"
                    @click="exportReport('attendance', 'pdf')"
                    :loading="exporting"
                  >
                    PDF
                  </v-btn>
                </div>
              </div>

              <v-data-table
                :headers="attendanceHeaders"
                :items="attendanceReport"
                :loading="loading"
                class="elevation-1"
                density="comfortable"
              >
                <template #[`item.name`]="{ item }">
                  <div class="font-weight-medium">{{ item.name }}</div>
                </template>

                <template #[`item.attendance_rate`]="{ item }">
                  <div class="d-flex align-center">
                    <v-progress-linear
                      :model-value="item.attendance_rate"
                      :color="getAttendanceColor(item.attendance_rate)"
                      height="8"
                      rounded
                      class="mr-3"
                      style="max-width: 100px;"
                    />
                    <span :class="`font-weight-bold text-${getAttendanceColor(item.attendance_rate)}`">
                      {{ item.attendance_rate }}%
                    </span>
                  </div>
                </template>

                <template #[`item.status`]="{ item }">
                  <v-chip
                    :color="item.attendance_rate >= 80 ? 'success' : item.attendance_rate >= 60 ? 'warning' : 'error'"
                    size="small"
                    variant="flat"
                  >
                    {{ item.attendance_rate >= 80 ? 'Excellent' : item.attendance_rate >= 60 ? 'Good' : 'Needs Improvement' }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-card-text>
          </v-window-item>

          <!-- Meetings Report Tab -->
          <v-window-item value="meetings">
            <v-card-text>
              <div class="d-flex justify-space-between align-center mb-4">
                <h3 class="text-h6">Meetings Analysis</h3>
                <div class="d-flex gap-2">
                  <v-btn
                    color="success"
                    variant="outlined"
                    prepend-icon="mdi-file-delimited"
                    @click="exportReport('meetings', 'csv')"
                    :loading="exporting"
                  >
                    CSV
                  </v-btn>
                  <v-btn
                    color="success"
                    variant="outlined"
                    prepend-icon="mdi-file-excel"
                    @click="exportReport('meetings', 'excel')"
                    :loading="exporting"
                  >
                    Excel
                  </v-btn>
                  <v-btn
                    color="error"
                    variant="outlined"
                    prepend-icon="mdi-file-pdf-box"
                    @click="exportReport('meetings', 'pdf')"
                    :loading="exporting"
                  >
                    PDF
                  </v-btn>
                </div>
              </div>

              <v-row class="mb-4">
                <v-col cols="12" md="4" v-for="(stat, key) in meetingsStats" :key="key">
                  <v-card class="text-center pa-4" elevation="2">
                    <v-icon :size="48" :color="stat.color" class="mb-2">{{ stat.icon }}</v-icon>
                    <div class="text-h5 font-weight-bold">{{ stat.value }}</div>
                    <div class="text-subtitle-2 text-medium-emphasis">{{ stat.label }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <v-data-table
                :headers="meetingsHeaders"
                :items="meetingsReport.meetings || []"
                :loading="loading"
                class="elevation-1"
                density="comfortable"
              >
                <template #[`item.title`]="{ item }">
                  <div class="font-weight-medium">{{ item.title }}</div>
                </template>

                <template #[`item.start_datetime`]="{ item }">
                  <div>{{ formatDateTime(item.start_datetime) }}</div>
                </template>

                <template #[`item.status`]="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    size="small"
                    variant="flat"
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-card-text>
          </v-window-item>

          <!-- Resolutions Report Tab -->
          <v-window-item value="resolutions">
            <v-card-text>
              <div class="d-flex justify-space-between align-center mb-4">
                <h3 class="text-h6">Resolutions Report</h3>
                <div class="d-flex gap-2">
                  <v-btn
                    color="success"
                    variant="outlined"
                    prepend-icon="mdi-file-delimited"
                    @click="exportReport('resolutions', 'csv')"
                    :loading="exporting"
                  >
                    CSV
                  </v-btn>
                  <v-btn
                    color="success"
                    variant="outlined"
                    prepend-icon="mdi-file-excel"
                    @click="exportReport('resolutions', 'excel')"
                    :loading="exporting"
                  >
                    Excel
                  </v-btn>
                  <v-btn
                    color="error"
                    variant="outlined"
                    prepend-icon="mdi-file-pdf-box"
                    @click="exportReport('resolutions', 'pdf')"
                    :loading="exporting"
                  >
                    PDF
                  </v-btn>
                </div>
              </div>

              <v-row class="mb-4">
                <v-col cols="12" md="6">
                  <v-card class="pa-4" elevation="2">
                    <h4 class="text-subtitle-1 mb-3">Resolutions by Status</h4>
                    <div v-for="(count, status) in resolutionsReport.summary?.by_status" :key="status" class="mb-2">
                      <div class="d-flex justify-space-between align-center">
                        <span class="text-capitalize">{{ status }}</span>
                        <v-chip size="small" :color="getStatusColor(status)">{{ count }}</v-chip>
                      </div>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card class="pa-4" elevation="2">
                    <h4 class="text-subtitle-1 mb-3">Summary</h4>
                    <div class="mb-2">
                      <div class="d-flex justify-space-between align-center">
                        <span class="font-weight-medium">Total Resolutions</span>
                        <v-chip size="small" color="primary">{{ resolutionsReport.summary?.total || 0 }}</v-chip>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <v-data-table
                :headers="resolutionsHeaders"
                :items="resolutionsReport.resolutions || []"
                :loading="loading"
                class="elevation-1"
                density="comfortable"
              >
                <template #[`item.title`]="{ item }">
                  <div class="font-weight-medium">{{ item.title }}</div>
                </template>

                <template #[`item.status`]="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    size="small"
                    variant="flat"
                  >
                    {{ item.status }}
                  </v-chip>
                </template>

                <template #[`item.created_at`]="{ item }">
                  <div>{{ formatDateTime(item.created_at) }}</div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-window-item>

          <!-- Comprehensive Report Tab -->
          <v-window-item value="comprehensive">
            <v-card-text>
              <div class="text-center py-8">
                <v-icon size="100" color="primary" class="mb-4">mdi-chart-box-outline</v-icon>
                <h3 class="text-h5 mb-4">Generate Comprehensive Board Report</h3>
                <p class="text-body-1 text-medium-emphasis mb-6">
                  Download a complete PDF report with all board activities, statistics, and analytics
                </p>
                <v-btn
                  color="error"
                  size="large"
                  prepend-icon="mdi-file-pdf-box"
                  @click="exportComprehensiveReport"
                  :loading="exporting"
                >
                  Download Comprehensive Report (PDF)
                </v-btn>
                
                <v-divider class="my-8" />
                
                <v-row>
                  <v-col cols="12" md="6">
                    <v-card class="pa-6" elevation="2">
                      <v-icon size="48" color="info" class="mb-3">mdi-information-outline</v-icon>
                      <h4 class="text-h6 mb-3">What's Included</h4>
                      <v-list density="compact">
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Executive Summary</v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Meeting Statistics</v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Attendance Performance</v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Resolution Analysis</v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Board Member Performance</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card class="pa-6" elevation="2">
                      <v-icon size="48" color="success" class="mb-3">mdi-cog-outline</v-icon>
                      <h4 class="text-h6 mb-3">Report Features</h4>
                      <v-list density="compact">
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Professional PDF Format</v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Date Range Filtering</v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Detailed Tables & Charts</v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Branded Header & Footer</v-list-item-title>
                        </v-list-item>
                        <v-list-item>
                          <template #prepend>
                            <v-icon>mdi-check-circle</v-icon>
                          </template>
                          <v-list-item-title>Ready for Presentation</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-window-item>
        </v-window>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useNotifications } from '@/composables/useNotifications'

const { showSuccess, showError } = useNotifications()

// State
const loading = ref(false)
const exporting = ref(false)
const activeTab = ref('attendance')
const dateRange = ref({
  start: null,
  end: null
})
const selectedPeriod = ref('all')

// Reports data
const attendanceReport = ref([])
const meetingsReport = ref({ summary: {}, meetings: [] })
const resolutionsReport = ref({ summary: {}, resolutions: [] })

// Period options
const periodOptions = [
  { title: 'All Time', value: 'all' },
  { title: 'Last 30 Days', value: '30days' },
  { title: 'Last 90 Days', value: '90days' },
  { title: 'Last 6 Months', value: '6months' },
  { title: 'This Year', value: 'year' },
  { title: 'Custom Range', value: 'custom' }
]

// Table headers
const attendanceHeaders = [
  { title: 'Name', key: 'name', sortable: true },
  { title: 'Total Meetings', key: 'total_meetings', sortable: true, align: 'center' },
  { title: 'Attended', key: 'attended', sortable: true, align: 'center' },
  { title: 'Absent', key: 'absent', sortable: true, align: 'center' },
  { title: 'Attendance Rate', key: 'attendance_rate', sortable: true, align: 'center' },
  { title: 'Status', key: 'status', sortable: false, align: 'center' }
]

const meetingsHeaders = [
  { title: 'Title', key: 'title', sortable: true },
  { title: 'Date', key: 'start_datetime', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Location', key: 'location', sortable: false }
]

const resolutionsHeaders = [
  { title: 'Title', key: 'title', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Type', key: 'type', sortable: true },
  { title: 'Created Date', key: 'created_at', sortable: true }
]

// Computed
const meetingsStats = computed(() => {
  const summary = meetingsReport.value.summary || {}
  return {
    total: {
      icon: 'mdi-calendar-check',
      color: 'primary',
      value: summary.total || 0,
      label: 'Total Meetings'
    },
    completed: {
      icon: 'mdi-check-circle',
      color: 'success',
      value: summary.by_status?.completed || 0,
      label: 'Completed'
    },
    scheduled: {
      icon: 'mdi-calendar-clock',
      color: 'warning',
      value: summary.by_status?.scheduled || 0,
      label: 'Scheduled'
    }
  }
})

// Methods
const updateDateRange = (period) => {
  const today = new Date()
  let start = null
  
  switch (period) {
    case '30days':
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30)
      break
    case '90days':
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90)
      break
    case '6months':
      start = new Date(today.getFullYear(), today.getMonth() - 6, today.getDate())
      break
    case 'year':
      start = new Date(today.getFullYear(), 0, 1)
      break
    case 'all':
      dateRange.value.start = null
      dateRange.value.end = null
      return
    case 'custom':
      return
  }
  
  if (start) {
    dateRange.value.start = start.toISOString().split('T')[0]
    dateRange.value.end = today.toISOString().split('T')[0]
  }
}

const refreshReports = async () => {
  loading.value = true
  try {
    await Promise.all([
      fetchAttendanceReport(),
      fetchMeetingsReport(),
      fetchResolutionsReport()
    ])
    showSuccess('Reports refreshed successfully')
  } catch (error) {
    console.error('Error refreshing reports:', error)
    showError('Failed to refresh reports')
  } finally {
    loading.value = false
  }
}

const fetchAttendanceReport = async () => {
  try {
    const params = {}
    if (dateRange.value.start) params.start_date = dateRange.value.start
    if (dateRange.value.end) params.end_date = dateRange.value.end
    
    const response = await api.get('/reports/attendance', { params })
    attendanceReport.value = response.data.attendance_report || []
  } catch (error) {
    console.error('Error fetching attendance report:', error)
    attendanceReport.value = []
  }
}

const fetchMeetingsReport = async () => {
  try {
    const params = {}
    if (dateRange.value.start) params.start_date = dateRange.value.start
    if (dateRange.value.end) params.end_date = dateRange.value.end
    
    const response = await api.get('/reports/meetings', { params })
    meetingsReport.value = response.data || { summary: {}, meetings: [] }
  } catch (error) {
    console.error('Error fetching meetings report:', error)
    meetingsReport.value = { summary: {}, meetings: [] }
  }
}

const fetchResolutionsReport = async () => {
  try {
    const params = {}
    if (dateRange.value.start) params.start_date = dateRange.value.start
    if (dateRange.value.end) params.end_date = dateRange.value.end
    
    const response = await api.get('/reports/resolutions', { params })
    resolutionsReport.value = response.data || { summary: {}, resolutions: [] }
  } catch (error) {
    console.error('Error fetching resolutions report:', error)
    resolutionsReport.value = { summary: {}, resolutions: [] }
  }
}

const exportReport = async (reportType, format) => {
  exporting.value = true
  try {
    const params = { format }
    if (dateRange.value.start) params.start_date = dateRange.value.start
    if (dateRange.value.end) params.end_date = dateRange.value.end
    
    const response = await api.get(`/reports/export/${reportType}`, {
      params,
      responseType: 'blob'
    })
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    
    // Determine file extension
    let extension = format
    if (format === 'excel') extension = 'xlsx'
    
    const timestamp = new Date().toISOString().split('T')[0]
    link.setAttribute('download', `${reportType}_report_${timestamp}.${extension}`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
    
    showSuccess(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report exported successfully`)
  } catch (error) {
    console.error('Error exporting report:', error)
    showError('Failed to export report')
  } finally {
    exporting.value = false
  }
}

const exportComprehensiveReport = async () => {
  exporting.value = true
  try {
    const params = {}
    if (dateRange.value.start) params.start_date = dateRange.value.start
    if (dateRange.value.end) params.end_date = dateRange.value.end
    
    const response = await api.get('/reports/export/comprehensive', {
      params,
      responseType: 'blob'
    })
    
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    
    const timestamp = new Date().toISOString().split('T')[0]
    link.setAttribute('download', `comprehensive_board_report_${timestamp}.pdf`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
    
    showSuccess('Comprehensive report downloaded successfully')
  } catch (error) {
    console.error('Error exporting comprehensive report:', error)
    showError('Failed to export comprehensive report')
  } finally {
    exporting.value = false
  }
}

const getAttendanceColor = (rate) => {
  if (rate >= 80) return 'success'
  if (rate >= 60) return 'warning'
  return 'error'
}

const getStatusColor = (status) => {
  const colors = {
    scheduled: 'info',
    in_progress: 'warning',
    completed: 'success',
    cancelled: 'error',
    draft: 'grey',
    voting: 'warning',
    approved: 'success',
    rejected: 'error',
    implemented: 'primary'
  }
  return colors[status] || 'grey'
}

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A'
  const date = new Date(dateTimeString)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// Lifecycle
onMounted(() => {
  refreshReports()
})
</script>

<style scoped>
.reports-page {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
}

.stat-card {
  border-radius: 8px;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.gap-2 {
  gap: 8px;
}
</style>
